import {
  HandleBannedFn,
  HandleUnauthorizedFn,
  ReqConfig,
  RequestMethod,
  RequestTokenFn,
} from '../model'
import queryString from 'qs'
import { appConfig } from '@config'
import { useSessionFingerprint } from '../useSessionFingerprint'
import { asyncFlow } from '../http/utils'
import { transformBody } from '../http/transform-body'
import { execHttpRequest } from '../http/exec-request'
import { handleErrors } from '../http/handle-errors'
import { handleApiResponse } from '../http/handle-response'

interface RequestFactoryConfig {
  method: RequestMethod
  requestToken: RequestTokenFn
  handleUnauthorized: HandleUnauthorizedFn
  handleBanned: HandleBannedFn
  initialRequestConfig?: Partial<ReqConfig>
}

export const requestFactory =
  (config: RequestFactoryConfig) =>
  <TResponse, TBody = unknown>(
    url: string,
    reqConfig: Partial<ReqConfig> = {
      encodeValuesOnly: true,
      sendDecodedQuery: false,
    },
  ) =>
  async (body?: TBody): Promise<TResponse> => {
    const sessionFingerprint = useSessionFingerprint()
    const apiUrl = getApiUrl(url, reqConfig)

    return (await asyncFlow(
      transformBody<TBody>(body),
      execHttpRequest(config.requestToken),
      handleErrors(config.handleUnauthorized, config.handleBanned),
      handleApiResponse(),
    )({
      apiUrl,
      reqConfig: {
        ...config.initialRequestConfig,
        ...reqConfig,
      },
      method: config.method,
      sessionFingerprint,
      isUnauthorized: false,
      isBanned: false,
    })) as TResponse
  }

const getApiUrl = (url: string, reqConfig?: Partial<ReqConfig>): string => {
  const fullUrl = `${reqConfig?.apiUrl ?? appConfig.apiUrl}${url}${
    reqConfig?.query ? '?' : ''
  }${queryString.stringify(reqConfig?.query || {}, { encodeValuesOnly: reqConfig?.encodeValuesOnly, arrayFormat: 'comma' })}`

  if (reqConfig?.sendDecodedQuery) {
    return decodeURIComponent(fullUrl)
  }

  return fullUrl
}
